import { createSelector } from 'reselect';
import { Plan, PlannedTwinID, PlanResultBand } from '@ynomia/core/dist/blueprint';
import map from './map';
import {
  getAssetManagerSelectedPlanId,
} from '.';
import { UNKNOWN_BAND, UNKNOWN_BAND_ID } from '../config/constants';

export const getPlannedTwinIDsArray = createSelector(
  [map.plannedTwinIDsKeyedById],
  (plannedTwinIDsKeyedById) => {
    const keys = [...plannedTwinIDsKeyedById.keys()];
    return keys.map(id => plannedTwinIDsKeyedById.get(id)) as Array<PlannedTwinID>;
  },
);

export const getPlannedTwinIDsKeyedByTwinId = createSelector(
  [getPlannedTwinIDsArray],
  (plans) => {
    const plannedTwinIdsKeyedByTwinId = new Map<string, PlannedTwinID[]>();
    plans.forEach((plan) => {
      const { twinID } = plan;
      if (!plannedTwinIdsKeyedByTwinId.has(twinID)) plannedTwinIdsKeyedByTwinId.set(twinID, []);
      plannedTwinIdsKeyedByTwinId.get(twinID)!.push(plan);
    });
    return plannedTwinIdsKeyedByTwinId;
  },
);

export const getPlanConfigurationsKeyedById = createSelector(
  [map.assetTypes],
  (assetTypes) => {
    if (!assetTypes) return new Map<string, Plan>();
    const mapping = assetTypes
      .map(
        at => at.plans
          ?.map(plan => [plan.id, plan] as [string, Plan])!,
      )
      .flat();
    return new Map<string, Plan>(mapping);
  },
);

export const getCurrentPlanConfiguration = createSelector(
  [getPlanConfigurationsKeyedById, getAssetManagerSelectedPlanId],
  (planConfigurationsKeyedById, selectedPlanId) => planConfigurationsKeyedById.get(selectedPlanId!),
);

export const getCurrentPlanBandsForCharts = createSelector(
  [getCurrentPlanConfiguration],
  currentPlanConfiguration => [
    ...currentPlanConfiguration?.bands || [],
    UNKNOWN_BAND,
  ],
);

export const getColorSchemeForBand = createSelector(
  [getCurrentPlanBandsForCharts],
  bands => bands.reduce((
    prev,
    band,
  ) => ({ ...prev, [band.label]: band.color }), {}),
);

export const getBandsKeyedById = createSelector(
  [getPlanConfigurationsKeyedById],
  (planConfigurationsKeyedById) => {
    const bandsKeyedById = new Map();

    planConfigurationsKeyedById.forEach((value) => {
      const { bands } = value;
      bands.forEach((band) => {
        bandsKeyedById.set(band.id, band);
      });
    });

    bandsKeyedById.set(UNKNOWN_BAND_ID, {
      id: UNKNOWN_BAND_ID,
      label: 'Unknown',
      color: '#e1e1e1',
      tolerance: 0,
    });

    return bandsKeyedById as Map<string, PlanResultBand>;
  },
);
